import { useState, ChangeEvent, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';

import usePagination from '@hooks/usePagination';
import { qrStatusTableHeader } from '@lib/data';
import { DELETE_PET_QR } from '@graphql/mutations';
import { SEE_PET_QR_LIST } from '@graphql/queries';
import { Query, Qr, Mutation } from '@graphql/types';

import { FilterBox, Contents } from '@components/styled/StyledComponents';
import Headline from '@components/layout/Headline';
import StyledSelect from '@components/styled/StyledSelect';
import TableHeader from '@components/share/Table/TableHeader';
import { TableBody, EmptyTable } from '@components/share/Table/table.style';
import QrStatusTableRow from '@components/feature/Operation/QrStatus/QrStatusTableRow';
import Pagination from '@components/layout/Pagination';
import Loader from '@components/share/Loader';

const QrStatus = () => {
    const [searchTerm, setSearchTerm] = useState('');
    // 페이지네이션 관련 상태
    const [take, setTake] = useState(10);
    const [preCursor, setPreCursor] = useState(0);
    const [nextCursor, setNextCursor] = useState(0);
    const [cursorOption, setCursorOption] = useState<boolean | null>(null);
    const [filter, setFilter] = useState({ order: 1 });

    const { data, loading } = useQuery<Pick<Query, 'seePetQrList'>>(
        SEE_PET_QR_LIST,
        {
            variables: {
                orderBy: +filter.order,
                take,
                preCursor,
                nextCursor,
                cursorOption,
                searchTerm
            },
            onError: (err) => {
                console.error(err);
            }
        }
    );
    const [deletePetQr] =
        useMutation<Pick<Mutation, 'deletePetQr'>>(DELETE_PET_QR);

    const petQrList = data?.seePetQrList?.petQrList ?? [];
    const totalLength = data?.seePetQrList?.totalLength ?? 0;

    const onFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilter((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const onDelete = useCallback(async (id: number) => {
        if (!window.confirm('해당 QR을 삭제하시겠습니까?')) return;
        try {
            await deletePetQr({
                variables: {
                    qrId: id
                },
                onCompleted: () => toast.success('삭제되었습니다.'),
                refetchQueries: [SEE_PET_QR_LIST]
            });
        } catch (err) {
            console.error(err);
        }
    }, []);

    // 페이지네이션
    const {
        pageIndex,
        setPageIndex,
        pageGroupLength,
        prevPageHandler,
        nextPageHandler,
        prevPageGroupHandler,
        nextPageGroupHandler,
        resetPaginationHandler,
        disabled
    } = usePagination(
        take,
        totalLength,
        setPreCursor,
        setNextCursor,
        setCursorOption,
        petQrList,
        'qrId'
    );

    return (
        <Contents>
            <Headline
                title="QR 현황"
                subTitle="QR 발행 현황 조회 및 관리"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchCount={totalLength}
                SEARCH
            />
            {/*  필터 영역 */}
            <FilterBox>
                <StyledSelect
                    title="정렬"
                    name="order"
                    value={filter.order}
                    onChange={onFilterChange}
                    options={[
                        { title: '내림차순', value: 1 },
                        { title: '오름차순', value: 2 }
                    ]}
                    DATA_VALUE
                />
            </FilterBox>
            <TableHeader header={qrStatusTableHeader} />

            <TableBody $minHeight={500}>
                {loading && <Loader />}
                {totalLength === 0 ? (
                    <EmptyTable>발행된 QR 코드가 없습니다.</EmptyTable>
                ) : (
                    petQrList
                        .slice(pageIndex, pageIndex + take)
                        .map((data, idx) => (
                            <QrStatusTableRow
                                key={`${data?.qrId}-${idx}-qr-status-list`}
                                header={qrStatusTableHeader}
                                data={data as Qr}
                                onDelete={onDelete}
                            />
                        ))
                )}
            </TableBody>

            {totalLength > 0 && (
                <Pagination
                    take={take}
                    setTake={setTake}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                    pageGroupLength={pageGroupLength}
                    totalLength={totalLength}
                    prevPageHandler={prevPageHandler}
                    nextPageHandler={nextPageHandler}
                    prevPageGroupHandler={prevPageGroupHandler}
                    nextPageGroupHandler={nextPageGroupHandler}
                    resetPaginationHandler={resetPaginationHandler}
                    disabled={disabled}
                />
            )}
        </Contents>
    );
};

export default QrStatus;
